import { fetchChatMessages, fetchEventNews, fetchNotifications, fetchUpdates, fetchEventMeetings } from '../../data/fetch';
import { selectLocate } from '../../data/hooks/i18nHooks';
import { askPermission } from './askPermission';
import { getPushNotificationsClientInstance } from './getPushNotificationsClientInstance';
import { rejectPushNotifications } from './rejectPushNotifications';
import { canAskPermission, getSubscriptionId, isNotificationsRejected, isSubscribed, setNotificationsRejected, storeSubscriptionId } from './storage';
import { unregisterOldNotificationsSystem } from './unregisterOldNotificationsSystem';

export async function enablePushNotifications() {
    if (isNotificationsRejected()) {
        if (isSubscribed()) {
            rejectPushNotifications();
        }
        return;
    }

    const client = getPushNotificationsClientInstance();

    try {
        // Wait until user is logged in
        await new Promise((resolve, reject) => {
            let unsubscribe = app.store.observe(
                (state) => state.session.hasUser,
                (hasUser) => {
                    if (hasUser) {
                        unsubscribe();
                        resolve();
                    }
                }
            );
            app.onShutdown(unsubscribe);
            app.onShutdown(() => reject('User never logged in'));
        });

        // remove old service workers
        await unregisterOldNotificationsSystem();

        // listen for notifications
        client.onNotificationReceived((payload) => {
            // invalidate cache depending on notification content
            fetchNotifications.cache.clear();

            if (payload?.data?.chatId) {
                // invalidate chat cache
                console.log('invalidate chat cache');
                fetchChatMessages.cache.clear();
            } else if (payload?.data?.newsId) {
                // invalidate news cache
                fetchEventNews.cache.clear();
            } else if (payload?.data?.meetingId) {
                // invalidate meetings cache
                fetchEventMeetings.cache.clear();
            } else {
                // fetch updates
                fetchUpdates();
            }

            app.store.dispatch(app.actions.pushNotifications.received(payload.data));
        });

        // ask permission (if needed and allowed by our rules)
        if (!client.hasPermission() && !client.isPermissionAsked() && canAskPermission()) {
            const accepted = await askPermission();
            if (!accepted) return;
        }

        // if no permission after asking, stop here
        if (!client.hasPermission()) return;

        // subscribe
        acceptPushNotifications();
    } catch (error) {
        console.error(error);
    }
}

export async function acceptPushNotifications() {
    setNotificationsRejected(false);

    const client = getPushNotificationsClientInstance();

    await client.requestPermission();
    await client.subscribe();

    // register device
    if (!getSubscriptionId()) {
        const subscription = await client.getSubscription(),
            { woosh_id: subscriptionId } = await app.api.users.registerDevice(subscription);
        storeSubscriptionId(subscriptionId);
    }

    // Listen for language changes
    const unobserveLocale = app.store.observe(selectLocate, (locale) => client.setLang(locale));
    app.onShutdown(unobserveLocale);
}
